import { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import './ImageEditModal.css';

/* 크롭된 픽셀 영역을 canvas → Blob으로 변환 */
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', reject);
    img.setAttribute('crossOrigin', 'anonymous');
    img.src = url;
  });

const getCroppedBlob = async (imageSrc, pixelCrop, mimeType = 'image/jpeg') => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y,
    pixelCrop.width, pixelCrop.height,
    0, 0,
    pixelCrop.width, pixelCrop.height,
  );
  return new Promise((resolve) => canvas.toBlob(resolve, mimeType, 0.95));
};

const ASPECT_OPTIONS = [
  { label: '자유', value: null },
  { label: '16 : 9', value: 16 / 9 },
  { label: '4 : 3',  value: 4 / 3 },
  { label: '1 : 1',  value: 1 },
  { label: '3 : 4',  value: 3 / 4 },
];

function ImageEditModal({ file, assetLabel, onConfirm, onCancel }) {
  const previewUrl = URL.createObjectURL(file);

  const [crop, setCrop]                     = useState({ x: 0, y: 0 });
  const [zoom, setZoom]                     = useState(1);
  const [aspect, setAspect]                 = useState(null);          // null = 자유
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isProcessing, setIsProcessing]     = useState(false);

  const onCropComplete = useCallback((_, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) { onConfirm(file); return; }
    setIsProcessing(true);
    try {
      const blob = await getCroppedBlob(previewUrl, croppedAreaPixels, file.type || 'image/jpeg');
      const resultFile = new File([blob], file.name, { type: blob.type });
      onConfirm(resultFile);
    } catch {
      onConfirm(file); // 실패하면 원본 그대로
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="iem-backdrop" onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="iem-box">

        {/* 헤더 */}
        <div className="iem-title">
          <span>🖼 {assetLabel} 변경</span>
          <span className="iem-title-sub">드래그로 위치, 스크롤로 확대/축소</span>
        </div>

        {/* 비율 선택 */}
        <div className="iem-aspect-row">
          <span className="iem-label">비율</span>
          {ASPECT_OPTIONS.map((opt) => (
            <button
              key={opt.label}
              type="button"
              className={`iem-aspect-btn${aspect === opt.value ? ' active' : ''}`}
              onClick={() => setAspect(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* 크롭 영역 */}
        <div className="iem-crop-container">
          <Cropper
            image={previewUrl}
            crop={crop}
            zoom={zoom}
            aspect={aspect ?? undefined}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            style={{
              containerStyle: { borderRadius: '8px' },
            }}
          />
        </div>

        {/* 줌 슬라이더 */}
        <div className="iem-zoom-row">
          <span className="iem-label">확대</span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="iem-zoom-slider"
          />
          <span className="iem-zoom-val">{zoom.toFixed(1)}x</span>
        </div>

        {/* 버튼 */}
        <div className="iem-actions">
          <button type="button" className="iem-btn-cancel" onClick={onCancel}>
            취소
          </button>
          <button
            type="button"
            className="iem-btn-confirm"
            onClick={handleConfirm}
            disabled={isProcessing}
          >
            {isProcessing ? '처리 중...' : '✅ 이미지 변경하기'}
          </button>
        </div>

      </div>
    </div>
  );
}

export default ImageEditModal;

